import { MagnifyingGlassCircleIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Pagination from "shared/Pagination/Pagination";
import DashboardMenu from "../../DashboardPage/DashboardMenu";
import { adminGetAllVideosSlider, deleteVideoSlider } from "redux/features/Admin/videoSlider/videoSliderSlice";

export interface ListVideosSliderPageProps {
  className?: string;
}

const ListVideosSliderPage: FC<ListVideosSliderPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);

  const { videos, totalCount, totalPages } = useAppSelector((state) => ({
    videos: state?.sliderVideo?.allVideos || [],
    totalCount: state?.sliderVideo?.totalCount || 0,
    totalPages: state?.sliderVideo?.totalPages || 0,
  }));

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        await dispatch(adminGetAllVideosSlider(currentPage));
      } catch (error) {
        console.error("Failed to fetch videos:", error);
      }
    };
    fetchVideos();
  }, [dispatch, currentPage]);

  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any | null>(null);

  const filteredVideos = Array.isArray(videos)
    ? videos.filter((video) => video?.title?.toLowerCase().includes(searchTerm.toLowerCase()))
    : [];

  const handleDeleteModalOpen = (video: any) => {
    setSelectedVideo(video);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedVideo?.id) {
      try {
        await dispatch(deleteVideoSlider(selectedVideo.id));
        await dispatch(adminGetAllVideosSlider(currentPage));
        setDeleteModalOpen(false);
      } catch (error) {
        console.error("Failed to delete video:", error);
      }
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <ButtonPrimary href="/admin/dashboard/settings/add-slider-video">Ajouter un vidéo</ButtonPrimary>
            <div className="relative flex">
              <Input
                type="text"
                placeholder="Rechercher par nom"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <MagnifyingGlassCircleIcon className="absolute w-6 h-6 text-gray-500 transform -translate-y-1/2 right-3 top-1/2" />
            </div>
          </div>

          {deleteModalOpen && selectedVideo && (
            <DeleteModal
              open={deleteModalOpen}
              handleClose={() => setDeleteModalOpen(false)}
              handleDelete={handleDeleteConfirm}
              category={{
                id: selectedVideo.id,
                designation: selectedVideo.title,
              }}
            />
          )}

          <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 sm:px-6 lg:px-8">
              <div className="overflow-hidden">
                <table className="min-w-full text-sm font-light text-center">
                  <thead className="font-medium text-white border-b bg-neutral-800 dark:border-neutral-500 dark:bg-neutral-900">
                    <tr>
                      <th scope="col" className="px-6 py-4">
                        Titre
                      </th>
                      <th scope="col" className="px-6 py-4">
                        Consulter
                      </th>
                      <th scope="col" className="px-6 py-4">
                        Modifier
                      </th>
                      <th scope="col" className="px-6 py-4">
                        Supprimer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVideos.map((item: any) => (
                      <tr className="border-b dark:border-neutral-500" key={item.id}>
                        <td className="px-6 py-4 font-medium whitespace-nowrap">{item.title}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <a
                            href={`${process.env.REACT_APP_CLOUD_FRONT ?? ""}${item.file ?? ""}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="text-2xl cursor-pointer las la-eye"></i>
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link to={`/admin/dashboard/settings/update-slider-video/${item.id}`}>
                            <i className="text-2xl las la-edit"></i>
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <i
                            className="text-2xl cursor-pointer las la-trash-alt"
                            onClick={() => handleDeleteModalOpen(item)}
                          ></i>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end">
                  <span className="text-sm text-gray-700">{totalCount} éléments</span>
                </div>
                <div className="flex items-center justify-center mt-11">
                  {totalPages > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListVideosSliderPage;
